import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Cached as CachedIcon } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { useStyles } from "../../../assets/styles/Theme";
import SnackbarMessage from "../../common/SnackbarMessage";
import {
  MSG_NOTE_ENTER,
  MSG_NOTE_NOT_FOUND,
  MSG_NOTE_SEARCH_FAILED,
  MSG_NOTE_ALREADY_CLAIMED,
  MSG_NOTE_EXPIRED,
  MSG_NOTE_APPLIED_SUCCESS,
} from "../../../utils/sys/Messages";
import { searchCreditNote } from "../../../utils/cashier/searchCreditNote";
import {
  setCreditNoteSearchResult,
  setClaimedCreditNote,
  clearCreditNoteSearch,
  clearClaimedCreditNote,
  resetCreditNote,
  selectClaimedCreditNote,
} from "../../../store/ReduxCreditNoteSlice";

const Item = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: "8px",
}));

function CashierCreditNote() {
  const theme = useTheme();
  const styles = useStyles(theme);
  const dispatch = useDispatch();

  const [noteId, setNoteId] = useState("");
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};

  const searchResult = useSelector((state) => state.creditNote?.searchResult);
  const claimedNote = useSelector(selectClaimedCreditNote);

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(price) || 0);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleClear = () => {
    setNoteId("");
    dispatch(resetCreditNote());
  };

  const handleSearch = async () => {
    if (!noteId.trim()) {
      showSnackbar(MSG_NOTE_ENTER, "warning");
      return;
    }
    setLoading(true);
    dispatch(clearClaimedCreditNote());
    try {
      const result = await searchCreditNote({
        noteId: noteId.trim(),
        COMPCODE,
        LOCCODE,
        RESTCODE,
      });
      if (result.error === "empty") {
        showSnackbar(MSG_NOTE_ENTER, "warning");
        dispatch(clearCreditNoteSearch());
      } else if (result.error || !result.note) {
        showSnackbar(MSG_NOTE_NOT_FOUND, "error");
        dispatch(clearCreditNoteSearch());
      } else if (result.note.isExpired) {
        showSnackbar(MSG_NOTE_EXPIRED, "warning");
        dispatch(setCreditNoteSearchResult(result.note));
      } else if (result.note.isClaimed || result.note.isRefunded) {
        showSnackbar(MSG_NOTE_ALREADY_CLAIMED, "warning");
        dispatch(setCreditNoteSearchResult(result.note));
      } else {
        dispatch(setCreditNoteSearchResult(result.note));
      }
    } catch {
      showSnackbar(MSG_NOTE_SEARCH_FAILED, "error");
      dispatch(clearCreditNoteSearch());
    } finally {
      setLoading(false);
    }
  };

  const handleClaimNow = () => {
    if (!searchResult) {
      showSnackbar(MSG_NOTE_ENTER, "warning");
      return;
    }
    if (searchResult.isExpired) {
      showSnackbar(MSG_NOTE_EXPIRED, "warning");
      return;
    }
    if (searchResult.isClaimed || searchResult.isRefunded) {
      showSnackbar(MSG_NOTE_ALREADY_CLAIMED, "warning");
      return;
    }

    setClaiming(true);
    dispatch(
      setClaimedCreditNote({
        ...searchResult,
        appliedAmount: searchResult.amount,
      })
    );
    showSnackbar(MSG_NOTE_APPLIED_SUCCESS, "success");
    setClaiming(false);
  };

  const handleRemoveClaimed = () => {
    dispatch(clearClaimedCreditNote());
    showSnackbar("Note credit removed from bill.", "info");
  };

  const preview = searchResult;

  return (
    <Box sx={styles.cs_cash}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            sx={{ ...theme.typography.h2, color: theme.palette.text.primary }}
          >
            Credit/Exchange Notes
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box gap={1} display="flex" alignItems="center">
            <TextField
              label="CN / EX No."
              placeholder="CN / EX No."
              size="small"
              fullWidth
              value={noteId}
              onChange={(e) => setNoteId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="contained"
              size="large"
              sx={{ ...styles.btn_primary, minWidth: 100 }}
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
            </Button>
            <IconButton
              size="small"
              sx={styles.cs_summary_refresh_icon}
              aria-label="clear"
              onClick={handleClear}
            >
              <CachedIcon />
            </IconButton>
          </Box>
        </Grid>

        {preview && (
          <>
            <Grid item xs={12}>
              <Item
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.divider}` }}
              >
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      CN / EX No.
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {preview.returnno}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={preview.noteTypeLabel}
                      size="small"
                      color="primary"
                    />
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {preview.customerName || "—"}
                    </Typography>
                  </Grid> */}
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {formatPrice(preview.amount)}
                    </Typography>
                  </Grid>
                  {preview.refundStatusLabel && (
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={preview.refundStatusLabel}
                        size="small"
                        color={
                          preview.isExpired
                            ? "error"
                            : preview.isClaimed
                              ? "success"
                              : "warning"
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Item>
            </Grid>

            {!preview.isClaimed &&
              !preview.isRefunded &&
              !preview.isExpired &&
              !claimedNote && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={styles.btn_primary}
                  onClick={handleClaimNow}
                  disabled={claiming}
                >
                  {claiming ? "Processing…" : "Claim Now"}
                </Button>
              </Grid>
            )}
          </>
        )}

        {claimedNote && (
          <Grid item xs={12}>
            <Item>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <Typography variant="caption" fontWeight={600}>
                    Applied: {formatPrice(claimedNote.appliedAmount)}
                  </Typography>
                  
                </Grid>
                <IconButton
                  onClick={handleRemoveClaimed}
                  aria-label="remove note credit"
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Item>
          </Grid>
        )}
      </Grid>

      <SnackbarMessage
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default CashierCreditNote;
